import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const QuestionSets = () => {
  const navigate = useNavigate();
  const [sets, setSets] = useState([]);
  const [allSets, setAllSets] = useState([]); // Para el dropdown
  const [loading, setLoading] = useState(false);
  const [filterArea, setFilterArea] = useState('');
  const [searchSetId, setSearchSetId] = useState('');
  const [selectedSetId, setSelectedSetId] = useState(''); // Para dropdown
  const [selectedSet, setSelectedSet] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  useEffect(() => {
    loadSets();
  }, [filterArea, searchSetId]);

  useEffect(() => {
    loadAllSetsList();
  }, []);

  const loadAllSetsList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/question-sets/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllSets(response.data.sets || []);
    } catch (err) {
      console.error('Error loading sets list:', err);
    }
  };

  const loadSets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = filterArea ? `?area=${filterArea}` : '';
      
      const response = await axios.get(`${API_URL}/api/question-sets${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Filtrar por searchSetId si existe
      let filteredSets = response.data.sets;
      if (searchSetId) {
        filteredSets = filteredSets.filter(set => 
          set.set_id.toLowerCase().includes(searchSetId.toLowerCase())
        );
      }

      setSets(filteredSets);
    } catch (err) {
      console.error('Error loading question sets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSet = async (setId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/question-sets/${setId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedSet(response.data);
      setViewDialog(true);
    } catch (err) {
      console.error('Error loading set details:', err);
    }
  };

  const handleDeleteSet = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/question-sets/${selectedSet.set_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteDialog(false);
      setViewDialog(false);
      setSelectedSet(null);
      loadSets();
    } catch (err) {
      console.error('Error deleting set:', err);
    }
  };

  const getAreaLabel = (area) => {
    const labels = {
      matematicas: 'Matemáticas',
      lectura_critica: 'Lectura Crítica',
      sociales: 'Sociales y Ciudadanas',
      ciencias: 'Ciencias Naturales',
      ingles: 'Inglés',
    };
    return labels[area] || area;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Conjuntos de Preguntas (Question Sets)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/teacher/questions/new?from=sets')}
        >
          Crear Nuevo Set
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Los Question Sets son grupos de preguntas que comparten el mismo contexto (texto o imagen). 
        Útil para preguntas de lectura crítica o comprensión.
      </Alert>

      {/* Estadísticas */}
      {allSets.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
          <Typography variant="h6">
            📊 Total de Sets: <strong>{allSets.length}</strong>
            {filterArea && ` • Mostrando: ${sets.length}`}
          </Typography>
        </Paper>
      )}

      {/* Filtros y Buscador */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Filtro por Área */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filtrar por Área</InputLabel>
              <Select
                value={filterArea}
                onChange={(e) => setFilterArea(e.target.value)}
                label="Filtrar por Área"
              >
                <MenuItem value="">Todas las áreas</MenuItem>
                <MenuItem value="matematicas">Matemáticas</MenuItem>
                <MenuItem value="lectura_critica">Lectura Crítica</MenuItem>
                <MenuItem value="sociales">Sociales</MenuItem>
                <MenuItem value="ciencias">Ciencias</MenuItem>
                <MenuItem value="ingles">Inglés</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Buscar por Set ID */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Buscar por Set ID"
              placeholder="Ej: LC001, MAT-2024..."
              value={searchSetId}
              onChange={(e) => setSearchSetId(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Selector de Set */}
          <Grid item xs={12} sm={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Ir a un Set Específico</InputLabel>
              <Select
                value={selectedSetId}
                onChange={(e) => {
                  const setId = e.target.value;
                  setSelectedSetId(setId);
                  if (setId) {
                    handleViewSet(setId);
                  }
                }}
                label="Ir a un Set Específico"
              >
                <MenuItem value="">
                  <em>Selecciona un set...</em>
                </MenuItem>
                {allSets.map((s) => (
                  <MenuItem key={s.set_id} value={s.set_id}>
                    {s.set_id} - {getAreaLabel(s.area)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Botón para limpiar filtros */}
        {(filterArea || searchSetId) && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilterArea('');
                setSearchSetId('');
                setSelectedSetId('');
              }}
            >
              Limpiar Filtros
            </Button>
          </Box>
        )}
      </Paper>

      {/* Lista de Sets */}
      {loading ? (
        <Typography>Cargando...</Typography>
      ) : sets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No hay question sets disponibles. Crea uno nuevo asignando el mismo question_set_id a varias preguntas.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {sets.map((set) => (
            <Grid item xs={12} sm={6} md={4} key={set.set_id}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {set.set_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        IDs: {set.questions.map(q => q.id).join(', ')}
                      </Typography>
                    </Box>
                    <Chip label={getAreaLabel(set.area)} size="small" color="primary" />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {set.total_questions} {set.total_questions === 1 ? 'pregunta' : 'preguntas'}
                  </Typography>

                  {set.questions[0]?.context_text && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {set.questions[0].context_text}
                    </Typography>
                  )}

                  {set.questions[0]?.context_image && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <ImageIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Incluye imagen de contexto
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewSet(set.set_id)}
                  >
                    Ver Detalles
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setSelectedSet(set);
                      setDeleteDialog(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog: Ver detalles del set */}
      <Dialog 
        open={viewDialog} 
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Set: {selectedSet?.set_id}</Typography>
            <Chip label={getAreaLabel(selectedSet?.area)} color="primary" />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSet?.context_image && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <img 
                src={selectedSet.context_image} 
                alt="Contexto" 
                style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
              />
            </Box>
          )}

          {selectedSet?.context_text && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Contexto Compartido:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedSet.context_text}
              </Typography>
            </Paper>
          )}

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Preguntas del Set ({selectedSet?.questions?.length || 0}):
          </Typography>

          <List>
            {selectedSet?.questions?.map((q, index) => (
              <React.Fragment key={q.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={`ID: ${q.id}`} 
                        size="small" 
                        color="secondary"
                        variant="outlined"
                      />
                      <Chip 
                        label={`Orden: ${q.question_order}`} 
                        size="small" 
                        variant="outlined"
                      />
                      <IconButton 
                        edge="end" 
                        onClick={() => navigate(`/teacher/questions/edit/${q.id}?from=sets`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ pr: 20 }}>
                        {index + 1}. {q.question}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Chip label={q.difficulty === 'easy' ? 'Fácil' : q.difficulty === 'medium' ? 'Medio' : 'Difícil'} size="small" sx={{ mr: 1 }} />
                        <Chip label={q.status === 'active' ? 'Activa' : 'Inactiva'} size="small" color={q.status === 'active' ? 'success' : 'default'} />
                      </Box>
                    }
                  />
                </ListItem>
                {index < selectedSet.questions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Cerrar</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate(`/teacher/questions/new?from=sets&set_id=${selectedSet?.set_id}`)}
          >
            Agregar Pregunta al Set
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar eliminación */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Eliminar Set Completo</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el set <strong>{selectedSet?.set_id}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            Esto eliminará TODAS las {selectedSet?.total_questions || selectedSet?.questions?.length} preguntas del set. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleDeleteSet} color="error" variant="contained">
            Eliminar Set
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuestionSets;
